/**
 * ExecutiveHeader — the moment the owner walks into the room.
 *
 * Displays the top-line briefing: campaign name, business goal,
 * duration, current phase (== campaign status label), and readiness.
 *
 * Mobile: stacks vertically. Desktop: two-column overview with a
 * prominent status indicator on the right.
 */
import { Card } from "@/shared/ui/card";
import type { CampaignBlueprint } from "@/features/marketing-strategy/types";
import type { CampaignStatusBlock } from "../../types";

const GOAL_LABEL: Record<string, string> = {
  leads: "Generating qualified leads",
  sales: "Driving sales",
  awareness: "Growing awareness",
  appointments: "Booking appointments",
  website_traffic: "Growing website traffic",
  customer_retention: "Retaining customers",
};

const DURATION_LABEL: Record<string, string> = {
  two_weeks: "Two weeks",
  thirty_days: "30 days",
  ninety_days: "90 days",
  six_months: "Six months",
  twelve_months: "Twelve months",
};

export function ExecutiveHeader({
  blueprint,
  campaignStatus,
}: {
  blueprint: CampaignBlueprint;
  campaignStatus: CampaignStatusBlock;
}): JSX.Element {
  const readiness = blueprint.strategy?.campaign_health;
  const dotColor =
    campaignStatus.health === "green"
      ? "text-emerald-500"
      : campaignStatus.health === "amber"
        ? "text-amber-500"
        : campaignStatus.health === "red"
          ? "text-rose-500"
          : "text-muted-foreground";

  return (
    <section data-testid="room-executive-header">
      <div className="mb-6">
        <p className="text-sm uppercase tracking-widest text-muted-foreground mb-2">
          Campaign Room
        </p>
        <h1
          className="text-3xl sm:text-4xl lg:text-5xl font-serif tracking-tight leading-tight"
          data-testid="room-campaign-name"
        >
          {blueprint.commander_answers.promotion}
        </h1>
      </div>

      <Card className="p-6 sm:p-8">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
              Business Goal
            </p>
            <p
              className="text-base sm:text-lg font-medium"
              data-testid="room-business-goal"
            >
              {GOAL_LABEL[blueprint.commander_answers.business_goal] ??
                blueprint.commander_answers.business_goal}
            </p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
              Duration
            </p>
            <p className="text-base sm:text-lg font-medium" data-testid="room-duration">
              {DURATION_LABEL[blueprint.commander_answers.duration] ??
                blueprint.commander_answers.duration}
            </p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
              Current Phase
            </p>
            <p
              className="text-base sm:text-lg font-medium"
              data-testid="room-current-phase"
            >
              {campaignStatus.label}
            </p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
              Campaign Readiness
            </p>
            <div className="flex items-baseline gap-2">
              <span className={`text-2xl ${dotColor}`} aria-hidden>
                {readiness?.indicator ?? "🟢"}
              </span>
              <span
                className="text-base sm:text-lg font-medium"
                data-testid="room-readiness-label"
              >
                {readiness?.label ?? campaignStatus.label}
              </span>
              {typeof readiness?.readiness_pct === "number" && (
                <span
                  className="text-xs text-muted-foreground ml-1"
                  data-testid="room-readiness-pct"
                >
                  {readiness.readiness_pct}%
                </span>
              )}
            </div>
          </div>
        </div>
      </Card>
    </section>
  );
}
